import {i18n} from '@online-offer/i18n';
import {Grid, GridItem, Heading, Show, Text, VStack} from 'platform/foundation';
import {useLocale} from 'platform/locale';

import {isNotNilOrEmpty} from 'ramda-adjunct';

import {Nullish} from 'shared/web';

import {FeatureItem} from '../../components/FeatureItem/FeatureItem';
import {SectionPrint} from '../../components/SectionPrint/SectionPrint';
import {useGetOnlineOfferData} from '../../hooks/useGetOnlineOfferData';
import {CategorizedFeature, getFeatures} from '../../utils/getFeatures';
import {placeholderFallback} from '../../utils/placeholderFallback';

export function FeaturesPrint() {
  const {vehicle, alphaCatalogue} = useGetOnlineOfferData();
  const locale = useLocale();
  const features = getFeatures({
    vehicle,
    alphaCatalogue,
    language: locale.language,
  });

  const shouldShowFeatures = features.length > 0;
  const shouldShowAdditionalInformation = isNotNilOrEmpty(vehicle?.featuresByManufacturer);

  const renderCategory = (category: CategorizedFeature | Nullish) => {
    if (!category) {
      return null;
    }
    return (
      <VStack spacing={5}>
        <Heading size={3}>{placeholderFallback(category.label?.label)}</Heading>
        <VStack spacing={1}>
          {category.features.map((feature) => (
            <FeatureItem
              key={feature.const_key}
              data-testid={feature.const_key}
              title={feature.labels?.[0]?.label}
              isHighlighted={feature.isHighlighted}
            />
          ))}
        </VStack>
      </VStack>
    );
  };

  if (!shouldShowFeatures && !shouldShowAdditionalInformation) {
    return null;
  }

  return (
    <SectionPrint heading={i18n.t('features.heading')}>
      <VStack spacing={7}>
        <Show when={shouldShowFeatures}>
          <Grid columns={2} horizontalSpacing={8}>
            <GridItem span={1}>{renderCategory(features[0])}</GridItem>
            <GridItem span={1}>
              <VStack spacing={9}>{features.slice(1).map(renderCategory)}</VStack>
            </GridItem>
          </Grid>
        </Show>
        <Show when={shouldShowAdditionalInformation}>
          <VStack spacing={2}>
            <Text size="large" alternative>
              {i18n.t('features.additionalInformation')}
            </Text>
            <Text size="small">{vehicle?.featuresByManufacturer}</Text>
          </VStack>
        </Show>
      </VStack>
    </SectionPrint>
  );
}
